import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getPartyBreakdown } from '../../api/politicians'
import clsx from 'clsx'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const currentParty = searchParams.get('party')

  const { data: breakdown } = useQuery({
    queryKey: ['partyBreakdown'],
    queryFn: getPartyBreakdown,
  })

  const senate = breakdown?.senate || { D: 0, R: 0, I: 0 }
  const house = breakdown?.house || { D: 0, R: 0, I: 0 }

  const handlePartyFilter = (party: string | null) => {
    if (party) {
      navigate(`/search?party=${party}`)
    } else {
      navigate('/search')
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar panel */}
      <aside
        role="navigation"
        className={clsx(
          'fixed top-0 left-0 h-full z-30 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen p-6 transition-transform duration-300',
          'lg:relative lg:translate-x-0 lg:block',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          !isOpen && 'hidden lg:block',
        )}>
        {/* Close button — mobile only */}
        <button
          className="lg:hidden absolute top-4 right-4 p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          onClick={onClose}
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Congress Overview
          </h3>
          <div className="space-y-4">
            <div className="card !p-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Senate</h4>
              <div className="flex items-center space-x-2 text-sm">
                <span className="party-badge party-badge-d">D: {senate.D ?? 0}</span>
                <span className="party-badge party-badge-r">R: {senate.R ?? 0}</span>
                <span className="party-badge party-badge-i">I: {senate.I ?? 0}</span>
              </div>
            </div>

            <div className="card !p-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-2">House</h4>
              <div className="flex items-center space-x-2 text-sm">
                <span className="party-badge party-badge-d">D: {house.D ?? 0}</span>
                <span className="party-badge party-badge-r">R: {house.R ?? 0}</span>
                <span className="party-badge party-badge-i">I: {house.I ?? 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Quick Filters
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => handlePartyFilter('R')}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                currentParty === 'R' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              <span aria-hidden="true">🔴 </span>Republicans
            </button>
            <button
              onClick={() => handlePartyFilter('D')}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                currentParty === 'D' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              <span aria-hidden="true">🔵 </span>Democrats
            </button>
            <button
              onClick={() => handlePartyFilter('I')}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                currentParty === 'I' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              <span aria-hidden="true">🟣 </span>Independents
            </button>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Explore
          </h3>
          <div className="space-y-2">
            <Link
              to="/lobbying"
              className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <span aria-hidden="true">🏛️ </span>Lobbying Dashboard
            </Link>
            <Link
              to="/finance"
              className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <span aria-hidden="true">💵 </span>Campaign Finance
            </Link>
            <Link
              to="/budget"
              className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <span aria-hidden="true">📈 </span>Federal Budget
            </Link>
            <Link
              to="/ticker/NVDA"
              className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <span aria-hidden="true">📊 </span>Company Lobbying
            </Link>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Resources
          </h3>
          <div className="space-y-2">
            <Link
              to="/about"
              className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              About PolitiTrack
            </Link>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3">
            Data Sources
          </h3>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <li>Congress.gov API</li>
            <li>FEC Campaign Finance</li>
            <li>Treasury Fiscal Data</li>
            <li>USAspending.gov</li>
            <li>QuiverQuant (Lobbying)</li>
          </ul>
        </div>
      </div>
      </aside>
    </>
  )
}
